/**
 * CapacitorPeripheralTransport — BLE peripheral (advertiser) role via the
 * custom BitchatPeripheral native plugin.
 *
 * This is the Stage 3b transport. Where CapacitorBleTransport plays the
 * CENTRAL role (scans for and connects to other advertisers),
 * CapacitorPeripheralTransport plays the PERIPHERAL role: it advertises the
 * Bitchat service so other centrals discover and connect to US, accepts
 * their writes, and notifies them with our outbound packets.
 *
 * Running both transports at once makes BisaWofa a full dual-role Bitchat
 * mesh node — exactly how the native Bitchat apps behave. MeshClient already
 * supports multiple simultaneous transports and dedups across them with its
 * Bloom filter, so no special coordination is needed here.
 *
 * Capabilities: canScan=false (scanning is the central transport's job),
 * canAdvertise=true, canRelay=true.
 *
 * Inbound bytes arrive per-central via the plugin's onWrite event. Each
 * central gets its own NotificationStreamAssembler because BLE writes from
 * different centrals interleave and don't share frame boundaries.
 */

import { BleTransport } from './BleTransport.js';
import { NotificationStreamAssembler } from '../NotificationStreamAssembler.js';
import { FragmentReassembler } from '../Fragmentation.js';
import { decode as decodePacket, TYPE } from '../MeshPacket.js';
import { decode as decodeAnnounce } from '../AnnouncePacket.js';
import { BitchatPeripheral } from '../plugins/bitchatPeripheral.js';

const SERVICE_UUID_MAINNET = 'f47b5e2d-4a9e-4c5a-9b3f-8e1d2c3a4b5c';
const CHARACTERISTIC_UUID   = 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d';

export class CapacitorPeripheralTransport extends BleTransport {
  constructor(opts) {
    super(opts);
    /** @type {Map<string, NotificationStreamAssembler>} per-central assemblers */
    this.assemblers = new Map();
    this.reassembler = new FragmentReassembler();
    this._listeners = [];
    this._knownPeers = new Set(); // peerIdHex we've surfaced, for onPeerLost bookkeeping
  }

  get capabilities() {
    return { canScan: false, canAdvertise: true, canRelay: true, name: 'Capacitor BLE (peripheral)' };
  }

  static async isSupported() {
    try {
      const { supported } = await BitchatPeripheral.isSupported();
      return !!supported;
    } catch (_) {
      return false;
    }
  }

  async start() {
    if (this.started) return;

    const { supported } = await BitchatPeripheral.isSupported();
    if (!supported) {
      throw new Error('BLE peripheral mode not supported on this device');
    }

    // Wire native events before we begin advertising so we don't miss the
    // first subscribe/write.
    this._listeners.push(
      await BitchatPeripheral.addListener('onWrite', (ev) => this._onWrite(ev)),
      await BitchatPeripheral.addListener('onSubscribe', (ev) => this._onSubscribe(ev)),
      await BitchatPeripheral.addListener('onUnsubscribe', (ev) => this._onUnsubscribe(ev)),
      await BitchatPeripheral.addListener('onStateChange', (ev) => {
        console.log('[CapacitorPeripheral] adapter state:', ev?.state);
      }),
    );

    await BitchatPeripheral.startAdvertising({
      serviceUuid: SERVICE_UUID_MAINNET,
      characteristicUuid: CHARACTERISTIC_UUID,
    });

    this.started = true;
    console.log('[CapacitorPeripheral] Advertising — discoverable as a Bitchat peer');
  }

  async stop() {
    if (!this.started) return;
    for (const l of this._listeners) {
      try { await l.remove(); } catch (_) {}
    }
    this._listeners = [];
    try { await BitchatPeripheral.stopAdvertising(); } catch (_) {}
    this.assemblers.clear();
    this.started = false;
    console.log('[CapacitorPeripheral] Stopped advertising');
  }

  /**
   * Broadcast packet bytes to every subscribed central. The peripheral
   * doesn't address individual peers at the link layer — it notifies all
   * subscribers and the mesh's TTL + recipient fields handle targeting.
   */
  async send(packetBytes, _recipientId) {
    if (!this.started) return;
    try {
      await BitchatPeripheral.notify({ data: _bytesToB64(packetBytes) });
    } catch (err) {
      console.warn('[CapacitorPeripheral] notify failed:', err.message || err);
    }
  }

  // ---------- native event handlers ----------

  _onWrite(ev) {
    if (!ev?.value) return;
    const centralId = ev.centralId || 'unknown';
    const chunk = _b64ToBytes(ev.value);

    let asm = this.assemblers.get(centralId);
    if (!asm) {
      asm = new NotificationStreamAssembler((bytes) => this._onFrame(bytes));
      this.assemblers.set(centralId, asm);
    }
    asm.feed(chunk);
  }

  _onSubscribe(ev) {
    console.log('[CapacitorPeripheral] central subscribed:', ev?.centralId);
    // A fresh central needs our signed announce to bind our sender ID,
    // exactly as the central-side flow does after connecting. Ask MeshClient
    // to (re)announce; it will dispatch through every active transport,
    // including this one (notify to subscribers).
    this.handlers.onNeedAnnounce();
  }

  _onUnsubscribe(ev) {
    const centralId = ev?.centralId || 'unknown';
    this.assemblers.delete(centralId);
    console.log('[CapacitorPeripheral] central unsubscribed:', centralId);
  }

  /**
   * Called by a per-central stream assembler with each complete frame.
   * Mirrors WebBluetoothTransport._onFrame: reassemble fragments, surface
   * announces as peers, hand every frame to MeshClient.
   */
  _onFrame(bytes) {
    let pkt;
    try { pkt = decodePacket(bytes); }
    catch (err) {
      console.warn('[CapacitorPeripheral] bad packet:', err.message);
      return;
    }

    if (pkt.type === TYPE.FRAGMENT) {
      const done = this.reassembler.ingest(pkt.senderId, pkt.payload);
      if (!done) return;
      this._onFrame(done.bytes);
      return;
    }

    if (pkt.type === TYPE.ANNOUNCE) {
      const announce = decodeAnnounce(pkt.payload);
      const idHex = _hex(pkt.senderId);
      this._knownPeers.add(idHex);
      this.handlers.onPeerSeen({
        id: pkt.senderId,
        idHex,
        nickname: announce.nickname || 'unknown',
        lastSeen: Date.now(),
        rssi: 0,
        transport: 'capacitor-ble (peripheral)',
      });
    }

    this.handlers.onPacket(bytes, pkt.senderId);
  }
}

// ---------- base64 <-> bytes (bridge marshalling) ----------

function _bytesToB64(u8) {
  let bin = '';
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin);
}

function _b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function _hex(u8) {
  return Array.from(u8, (b) => b.toString(16).padStart(2, '0')).join('');
}

export default CapacitorPeripheralTransport;
